/**
 * Local tool execution for the thin client.
 *
 * In the gateway architecture the BACKEND drives the agent loop and asks the CLI
 * to run tools (`tool_request`); this runner is the CLI's executor. It owns the
 * `@fim/futurex-tools` registry, validates the model's (untrusted) arguments with
 * each tool's Zod schema, gates mutating tools behind a permission prompt, and
 * runs them confined to the repo root. The descriptors it advertises are exactly
 * what the gateway feeds the model — the CLI is authoritative about its own tools.
 */
import { createToolRegistry, type ToolContext } from '@fim/futurex-tools';
import type { ToolDescriptor } from '@fim/futurex-protocol';
import type { Gate } from './permissions.js';

export type { Gate } from './permissions.js';

export interface ToolRunner {
  /** Tool descriptors to advertise to the gateway (name/description/params/mutating). */
  descriptors(): ToolDescriptor[];
  /** Human-readable one-line preview of a tool call, or null if the call is invalid. */
  preview(name: string, argsJson: string): string | null;
  /** Validate, (gate if mutating), and run a tool. Never throws — errors become output. */
  run(name: string, argsJson: string, gate: Gate): Promise<{ ok: boolean; output: string }>;
  /** Number of tools available (for banners). */
  readonly count: number;
}

export function createToolRunner(repoRoot: string): ToolRunner {
  const registry = createToolRegistry();
  const ctx: ToolContext = { repoRoot };

  const parse = (name: string, argsJson: string): { spec: ReturnType<typeof registry.get>; args?: unknown; error?: string } => {
    const spec = registry.get(name);
    if (!spec) return { spec, error: `unknown tool: ${name}` };
    try {
      return { spec, args: spec.schema.parse(JSON.parse(argsJson || '{}')) };
    } catch (err) {
      return { spec, error: `invalid arguments: ${err instanceof Error ? err.message : String(err)}` };
    }
  };

  return {
    count: registry.specs.length,
    descriptors() {
      return registry.specs.map((s) => ({
        name: s.name,
        description: s.description,
        parameters: s.parameters,
        mutating: s.mutating,
      }));
    },
    preview(name, argsJson) {
      const { spec, args, error } = parse(name, argsJson);
      if (!spec || error || args === undefined) return null;
      return spec.preview(args, ctx);
    },
    async run(name, argsJson, gate) {
      const { spec, args, error } = parse(name, argsJson);
      if (!spec) return { ok: false, output: error ?? `unknown tool: ${name}` };
      if (error || args === undefined) return { ok: false, output: error ?? 'invalid arguments' };
      const kind = spec.classify(args, ctx);
      if (kind !== null) {
        const approved = await gate({ tool: spec.name, kind, preview: spec.preview(args, ctx) });
        if (!approved) return { ok: false, output: 'denied by the user' };
      }
      try {
        return await spec.run(args, ctx);
      } catch (err) {
        return { ok: false, output: `tool error: ${err instanceof Error ? err.message : String(err)}` };
      }
    },
  };
}
